/**
 * Partner messages: the short shout one athlete posts to a training partner's Today page.
 * Every change answers with the full, fresh PartnerMessagesResponse, which replaces the cache.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { PartnerMessageInput, PartnerMessagesResponse } from '@gd/shared'
import { api } from './client'

export const partnerMessageKeys = {
  all: () => ['partner-messages'] as const,
  forAthlete: (athleteId: string | undefined) => ['partner-messages', athleteId] as const,
}

/** Partners check Today now and then; a new shout should land without a reload. */
const REFRESH_MS = 5 * 60_000

export function usePartnerMessages(athleteId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: partnerMessageKeys.forAthlete(athleteId),
    queryFn: ({ signal }) =>
      api.get('GET /api/partner-messages', {
        query: athleteId ? { athlete: athleteId } : undefined,
        signal,
      }),
    enabled: enabled && !!athleteId,
    refetchInterval: REFRESH_MS,
  })
}

function settle(queryClient: QueryClient) {
  return {
    onSuccess: (response: PartnerMessagesResponse) => {
      queryClient.setQueryData(partnerMessageKeys.forAthlete(response.athleteId), response)
    },
  }
}

export function useSendPartnerMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ recipientId, input }: { recipientId: string; input: PartnerMessageInput }) =>
      api.put('PUT /api/partner-messages/:recipientId', {
        params: { recipientId },
        body: input,
      }),
    ...settle(queryClient),
  })
}

export function useClearPartnerMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ recipientId }: { recipientId: string }) =>
      api.delete('DELETE /api/partner-messages/:recipientId', { params: { recipientId } }),
    ...settle(queryClient),
  })
}

/** Hides a shout from the viewer's Today at once; puts it back if the server says no. */
export function useDismissPartnerMessage(athleteId: string | undefined) {
  const queryClient = useQueryClient()
  const key = partnerMessageKeys.forAthlete(athleteId)
  return useMutation({
    mutationFn: ({ senderId }: { senderId: string }) =>
      api.post('POST /api/partner-messages/:senderId/dismiss', { params: { senderId } }),
    onMutate: async ({ senderId }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PartnerMessagesResponse>(key)
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          received: previous.received.filter((m) => m.senderAthleteId !== senderId),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    ...settle(queryClient),
  })
}
